import type { SalesMove, SalesMoveActionEvent } from "@sales-ops-amplify/shared";
import { useSalesMoveActions } from "@/hooks/useSalesMoves";

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function actionLabel(event: SalesMoveActionEvent): string {
  switch (event.action) {
    case "EXECUTE_PRIMARY":
      return "Primary action";
    case "COMPLETE":
      return "Completed";
    case "DISMISS":
      return "Dismissed";
    case "LATER":
      return "Later";
    case "NOT_RIGHT":
      return "Not right";
    case "DELEGATE":
      return "Delegated";
    case "UNDO":
      return "Undone";
    case "SYSTEM_COMPLETED":
      return "Completed (activity)";
    default:
      return event.action;
  }
}

type Props = {
  move: SalesMove;
};

export function SalesMoveHistorySection({ move }: Props) {
  const { data, isLoading, isError } = useSalesMoveActions(
    move.id,
    move.fingerprint,
  );
  const actions = data?.actions ?? [];

  return (
    <details className="mt-4 border-t border-border pt-3">
      <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground">
        History{actions.length > 0 ? ` (${actions.length})` : ""}
      </summary>
      <div className="mt-2 space-y-2">
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading history…</p>
        ) : null}
        {isError ? (
          <p className="text-xs text-muted-foreground">Could not load history.</p>
        ) : null}
        {!isLoading && !isError && actions.length === 0 ? (
          <p className="text-xs text-muted-foreground">No actions yet.</p>
        ) : null}
        <ul className="space-y-2">
          {actions.map((event) => {
            const simulated = event.sideEffects.some(
              (s) => s.status === "SIMULATED",
            );
            return (
              <li
                key={event.actionId}
                className="rounded-md border border-border/60 px-2.5 py-2 text-xs"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                  <span className="font-medium text-foreground">
                    {actionLabel(event)}
                  </span>
                  <time
                    className="text-muted-foreground"
                    dateTime={event.createdAt}
                  >
                    {formatWhen(event.createdAt)}
                  </time>
                </div>
                {event.message ? (
                  <p className="mt-0.5 text-foreground/80">{event.message}</p>
                ) : null}
                <p className="mt-0.5 text-muted-foreground">
                  {event.localOutcome}
                  {simulated ? " · simulated" : ""}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    </details>
  );
}
